import React, { useState } from 'react';
import { Check, Zap, Crown, Building2, Users } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../hooks/useAuth';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const PricingSection = () => {
  const { user } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [isAnnual, setIsAnnual] = useState(true);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      monthlyPrice: 0,
      annualPrice: 0,
      description: 'For individuals getting started',
      icon: Zap,
      features: [
        '10 free monthly credits',
        'Core platform features',
        'Web & mobile building',
        'Instant model access',
        'Community support'
      ],
      cta: 'Get Started',
      popular: false,
      gradient: 'from-slate-500 to-slate-600'
    },
    {
      id: 'standard',
      name: 'Standard',
      monthlyPrice: 20,
      annualPrice: 17,
      annualSavings: 36,
      description: 'For professionals building real apps',
      icon: Crown,
      features: [
        '100 monthly credits',
        'Private project hosting',
        'All AI models (Claude, GPT, Grok)',
        'GitHub integration',
        'Purchase extra credits',
        'Custom domains',
        'One-click deployment',
        'Email support'
      ],
      cta: 'Start Standard',
      popular: true,
      gradient: 'from-blue-500 to-cyan-400'
    },
    {
      id: 'pro',
      name: 'Pro',
      monthlyPrice: 200,
      annualPrice: 167,
      annualSavings: 396,
      description: 'For power users who need the best',
      icon: Building2,
      features: [
        '750 monthly credits',
        '1M context window',
        'Ultra thinking mode',
        'System prompt editing',
        'Custom AI agents',
        'Priority support',
        'Advanced integrations',
        'API access',
        'Priority queue'
      ],
      cta: 'Start Pro',
      popular: false,
      gradient: 'from-purple-500 to-pink-400'
    },
    {
      id: 'team',
      name: 'Team',
      monthlyPrice: 300,
      annualPrice: 250,
      annualSavings: 600,
      description: 'For teams building together',
      icon: Users,
      features: [
        '1,250 shared monthly credits',
        'All Pro features included',
        'Up to 5 team members',
        'Unified billing',
        'Real-time collaboration',
        'Team analytics dashboard',
        'Dedicated support',
        'Admin controls'
      ],
      cta: 'Start Team',
      popular: false,
      gradient: 'from-amber-500 to-orange-400'
    }
  ];

  const creditPackages = [
    { id: 'credits_275', name: '275', price: '$55', per: '$0.20', savings: null },
    { id: 'credits_550', name: '550', price: '$110', per: '$0.20', savings: null },
    { id: 'credits_1150', name: '1,150', price: '$230', per: '$0.20', savings: null },
    { id: 'credits_3000', name: '3,000', price: '$600', per: '$0.20', savings: null }
  ];

  const handleSubscribe = async (planId) => {
    if (planId === 'free') {
      toast({ title: 'Free Plan', description: 'You are already on the free plan!' });
      return;
    }
    if (!user) {
      toast({ title: 'Sign in required', description: 'Please sign in to subscribe.', variant: 'destructive' });
      return;
    }
    setLoadingPlan(planId);
    try {
      const response = await api.post('/checkout/subscription', { plan_id: planId, billing: isAnnual ? 'annual' : 'monthly' });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch {
      toast({ title: 'Test Mode', description: 'Checkout is in test mode. Configure Stripe live keys to enable payments.' });
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleBuyCredits = async (packageId) => {
    if (!user) {
      toast({ title: 'Sign in required', description: 'Please sign in to purchase credits.', variant: 'destructive' });
      return;
    }
    setLoadingPlan(packageId);
    try {
      const response = await api.post('/checkout/credits', { package_id: packageId });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch {
      toast({ title: 'Test Mode', description: 'Checkout is in test mode. Configure Stripe live keys to enable payments.' });
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div id="pricing" className="py-24 bg-background" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12" data-testid="pricing-header">
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-4">PRICING</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Start free. Scale as you grow. No hidden fees.
          </p>
        </div>

        {/* Annual Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12" data-testid="billing-toggle">
          <span className={`text-sm font-medium transition-colors ${!isAnnual ? 'text-white' : 'text-muted-foreground'}`}>Monthly</span>
          <button
            onClick={() => setIsAnnual(!isAnnual)}
            className={`relative w-14 h-7 rounded-full transition-colors ${isAnnual ? 'bg-blue-600' : 'bg-[#2e2e2e]'}`}
            data-testid="annual-toggle"
          >
            <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${isAnnual ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
          <span className={`text-sm font-medium transition-colors ${isAnnual ? 'text-white' : 'text-muted-foreground'}`}>
            Annual
            {isAnnual && <span className="ml-2 text-xs text-emerald-400 font-semibold">Save up to 17%</span>}
          </span>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {plans.map((plan) => {
            const IconComp = plan.icon;
            const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
            return (
              <div
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative rounded-3xl border-2 bg-card flex flex-col transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/5 ${
                  plan.popular
                    ? 'border-blue-500/50 shadow-2xl shadow-blue-500/10 scale-[1.02]'
                    : 'border-input hover:scale-[1.02]'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white px-6 py-1.5 rounded-full text-sm font-semibold shadow-lg shadow-blue-500/30">
                      Most Popular
                    </span>
                  </div>
                )}

                {/* Card Content - grows */}
                <div className="p-8 flex-1 flex flex-col">
                  <div className="text-center mb-8">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-r ${plan.gradient} flex items-center justify-center mx-auto mb-4`}>
                      <IconComp className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">{plan.name}</h3>
                    <div className="mb-3">
                      <span className={`text-5xl font-bold bg-gradient-to-r ${plan.gradient} bg-clip-text text-transparent`}>
                        ${price}
                      </span>
                      <span className="text-muted-foreground text-base ml-2">/month</span>
                    </div>
                    {isAnnual && plan.annualSavings && (
                      <p className="text-emerald-400 text-xs font-semibold">Save ${plan.annualSavings}/year</p>
                    )}
                    <p className="text-muted-foreground text-sm mt-2">{plan.description}</p>
                  </div>

                  <ul className="space-y-3 flex-1">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-foreground/80 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Button - pinned to bottom */}
                <div className="p-8 pt-0">
                  <Button
                    data-testid={`subscribe-${plan.id}-btn`}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loadingPlan === plan.id}
                    className={`w-full py-6 rounded-xl text-base font-semibold transition-all ${
                      plan.popular
                        ? 'bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white shadow-lg shadow-blue-500/25'
                        : 'bg-accent hover:bg-accent text-white'
                    }`}
                  >
                    {loadingPlan === plan.id ? 'Processing...' : plan.cta}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Credit Packages */}
        <div className="max-w-4xl mx-auto" id="enterprise" data-testid="enterprise-section">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-foreground mb-3">Pay As You Go</h3>
            <p className="text-muted-foreground">Need more credits? Purchase additional packs anytime.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="credit-packages">
            {creditPackages.map((pkg) => (
              <div
                key={pkg.id}
                data-testid={`credit-pkg-${pkg.id}`}
                className="border-2 border-border bg-card rounded-2xl p-6 text-center hover:border-blue-500/40 hover:shadow-lg hover:shadow-blue-500/5 transition-all cursor-pointer group"
                onClick={() => handleBuyCredits(pkg.id)}
              >
                <div className="text-lg font-bold text-foreground mb-0.5">{pkg.name} Credits</div>
                <div className="text-3xl font-bold text-blue-400 mb-1">{pkg.price}</div>
                <div className="text-xs text-muted-foreground mb-1">{pkg.per}/credit</div>
                {pkg.savings && (
                  <span className="inline-block bg-emerald-500/20 text-emerald-400 text-xs font-semibold px-2 py-0.5 rounded-full">
                    {pkg.savings}
                  </span>
                )}
                <Button
                  data-testid={`buy-${pkg.id}-btn`}
                  variant="outline"
                  size="sm"
                  disabled={loadingPlan === pkg.id}
                  className="mt-4 w-full border-input text-foreground/80 hover:bg-blue-500/10 hover:border-blue-500/40"
                >
                  {loadingPlan === pkg.id ? 'Processing...' : 'Buy Now'}
                </Button>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center p-8 bg-card rounded-2xl border border-border" data-testid="enterprise-contact">
            <h4 className="text-xl font-bold text-foreground mb-2">Enterprise</h4>
            <p className="text-muted-foreground text-sm mb-4">
              Need unlimited credits, custom AI models, SSO, or a dedicated account manager?
            </p>
            <a href="mailto:enterprise@supertool.ai" className="text-blue-400 hover:text-blue-300 font-semibold text-sm">
              Contact Sales &rarr;
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingSection;
